import { slugName } from "@phone-booth/content/layout";
import { useState } from "preact/hooks";
import { type Library, saveCategory, setRoute } from "./api.ts";
import { Close } from "./icons.tsx";
import { Button, Callout, Field, ScreenHead, ScreenTitle } from "./ui.tsx";
import { useAction } from "./use-action.ts";

interface Props {
  digit: string;
  library: Library;
  onClose: () => void;
  /** Handed the new category once both halves are stored. */
  onCreated: (category: string) => void;
}

/**
 * One step creates both halves of a number: the topic becomes a category, and
 * the digit is pointed at it. Splitting them would leave a staffer with a
 * category and no way to hear it.
 */
export function SetUpNumberScreen({ digit, library, onClose, onCreated }: Props) {
  const [title, setTitle] = useState("");
  const [refused, setRefused] = useState<{ title: string; detail?: string } | null>(
    null,
  );
  const { busy, error, setError, run } = useAction();

  function create(event: Event): void {
    event.preventDefault();
    const wanted = title.trim();
    const category = slugName(wanted);
    if (category === null) {
      setRefused({ title: "Use letters or numbers in the name." });
      return;
    }
    const taken = Object.entries(library.digits).find(([, name]) => {
      return name === category;
    });
    if (taken) {
      setRefused({
        title: `That name is already used on number ${taken[0]}. Pick a different one.`,
      });
      return;
    }
    void run(async () => {
      await saveCategory(category, wanted);
      await setRoute(digit, category);
      onCreated(category);
    });
  }

  return (
    <>
      <ScreenHead
        trailing={
          <button type="button" class="icon-btn" aria-label="Close" onClick={onClose}>
            <Close />
          </button>
        }
      />
      <form class="narrow" onSubmit={create}>
        <p class="big-digit">{digit}</p>
        <ScreenTitle centred>Set up number {digit}</ScreenTitle>
        <Field
          id="topic"
          label="Topic name"
          help="For example: Hiking tips, History facts"
        >
          <input
            id="topic"
            type="text"
            placeholder="Animal stories"
            value={title}
            disabled={busy}
            onInput={(event) => {
              setError(null);
              setRefused(null);
              setTitle((event.target as HTMLInputElement).value);
            }}
          />
        </Field>
        {(refused ?? error) && (
          <Callout tone="bad" title={refused?.title ?? error ?? ""}>
            {refused?.detail}
          </Callout>
        )}
        <Button
          tone="primary"
          full
          type="submit"
          busy={busy}
          busyLabel="Creating…"
          disabled={title.trim() === ""}
        >
          Create
        </Button>
      </form>
    </>
  );
}
